import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TableOptions } from "../../../../shared-kernel/components/Table";
import {
  createAdapterToConceptType,
  createAdapterToConceptTypeEndpoint,
  createAdapterToFieldConceptTypeEndpoint,
} from "../adapters/conceptType.adapter";
import {ConceptType, ConceptTypeEndpoint, EndpointConceptType, EndpointConceptTypeList} from "../domain/ConceptType.model";
import { appConfig } from "../../../../config";
import { createAdapterToFilterBackend } from "../../../../utilities/paramsAdapter";

const BASE_CONCEPT_TYPE_URL = '/entidadesfinancieras/tipoconceptos'
export const BASE_EXPORT_URL = `${appConfig.API_NODE_URL}/entidadesfinancieras/tipoconceptos/export`;

export const conceptTypeApi = createApi({
  reducerPath: "conceptTypeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: appConfig.API_NODE_URL,
    prepareHeaders: (headers: Headers) => {
      const APP_TOKEN = "app_token";
      const token = localStorage.getItem(APP_TOKEN);

      if (token) {
        headers.set("authorization", `${token}`);
      }

      return headers;
    },
    credentials: "include",
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: ["conceptTypes"],
  endpoints: (builder) => ({
    getConceptType: builder.query<ConceptType, number | undefined>({
      query: (id) => `${BASE_CONCEPT_TYPE_URL}/${id}`,
      transformResponse: (response: EndpointConceptType) => {
        return createAdapterToConceptType(response.Content)
      },
      providesTags: (_res, _err, id) => [{type: "conceptTypes", id: id?.toString()}],
    }),
    getConceptTypes: builder.query<any, TableOptions<ConceptType> | undefined>({
      query: (params: TableOptions<ConceptType>) => {
        const { page, pageSize, order, search, filters } = params || {};
        const adaptersFilter = filters?.map( f => createAdapterToFilterBackend(f,createAdapterToFieldConceptTypeEndpoint))
        const serializedFilters = adaptersFilter ? JSON.stringify(adaptersFilter) : undefined;
        const serializedOrder = order ? JSON.stringify({field:createAdapterToFieldConceptTypeEndpoint(order.field), order:order.order}) : undefined;
        return {
          url:`${BASE_CONCEPT_TYPE_URL}`,
          params: {
            page:page? page - 1: 0,
            pageSize,
            order: serializedOrder,
            search,
            filters:serializedFilters
          },
        }
      },
      providesTags: ["conceptTypes"],
      transformResponse: (response: EndpointConceptTypeList | undefined) => {
        const endpointResponse = response?.Content.map((conceptType: ConceptTypeEndpoint) =>
            createAdapterToConceptType(conceptType)
        );
        return endpointResponse;
      },
    }),
    createConceptType: builder.mutation<any, ConceptType>({
      query: (data) => ({
        url: `${BASE_CONCEPT_TYPE_URL}`,
        method: "POST",
        body: {
          "Content": createAdapterToConceptTypeEndpoint(data)
        },
      }),
      invalidatesTags: ["conceptTypes"],
    }),
    updateConceptType: builder.mutation<any, ConceptType>({
      query: (data: ConceptType) => ({
        url: `${BASE_CONCEPT_TYPE_URL}`,
        method: "PUT",
        body: {
          "Content": createAdapterToConceptTypeEndpoint(data)
        },
      }),
      invalidatesTags: (_res, _err, data: ConceptType) => ["conceptTypes", {type: 'conceptTypes', id: data?.id?.toString()}],
    }),
    deleteConceptType: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE_CONCEPT_TYPE_URL}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["conceptTypes"],
    }),
  }),
});

export const {
  useGetConceptTypesQuery,
  useGetConceptTypeQuery,
  useCreateConceptTypeMutation,
  useDeleteConceptTypeMutation,
  useUpdateConceptTypeMutation
} = conceptTypeApi;
